import type { InputFrame } from '../core/input';
import { characterDef, type CharacterId } from '../content/characters';
import type { PetAssignmentStation } from '../content/pets';
import { HOUSE_MODULE_BY_ID, rotatedFootprint, type HousePlacement } from './modules';

export type ObservatoryStationId = 'chronolab' | 'garden' | 'wardrobe' | 'pets' | 'timeline' | 'telescope';

export interface ObservatoryStation {
  id: ObservatoryStationId;
  name: string;
  description: string;
  x: number;
  z: number;
  radius: number;
}

/**
 * Interaction points are deliberately data-driven. New rooms can add stations
 * here without changing movement, prompting, collision, or the renderer.
 */
export const OBSERVATORY_STATIONS: readonly ObservatoryStation[] = [
  { id: 'chronolab', name: 'Chronolab', description: 'Improve starting rerolls.', x: -9.5, z: -5.5, radius: 3.2 },
  { id: 'wardrobe', name: 'Wardrobe & Arsenal', description: 'Manage your character and inventory.', x: -12.5, z: 5.5, radius: 2.8 },
  { id: 'pets', name: 'Companion Corner', description: 'Choose and manage your pets.', x: -2.5, z: 7.5, radius: 3 },
  { id: 'timeline', name: 'Timeline Gate', description: 'Prepare and begin a run.', x: 0, z: -8.2, radius: 3.1 },
  { id: 'garden', name: 'Temporal Garden', description: 'Cultivate restorative run supplies.', x: 11, z: -4.5, radius: 3.4 },
  { id: 'telescope', name: 'Chronoscope', description: 'A future window into distant eras.', x: 12.5, z: 7, radius: 2.6 },
] as const;

interface RectCollider { x0: number; x1: number; z0: number; z1: number }

/** Solid furniture. Outer walls and fences are represented by the map bounds. */
const COLLIDERS: readonly RectCollider[] = [
  { x0: -11.8, x1: -7.2, z0: -7.5, z1: -3.8 }, // Chronolab console
  { x0: -14.8, x1: -11.1, z0: 4.2, z1: 7.3 }, // wardrobe
  { x0: -4.2, x1: -0.7, z0: 6.1, z1: 9.1 }, // pet beds / storage
  { x0: -2.2, x1: 2.2, z0: -10.1, z1: -7.3 }, // timeline gate
  { x0: 8.2, x1: 13.8, z0: -6.4, z1: -2.8 }, // garden beds
  { x0: 11.2, x1: 14.3, z0: 5.5, z1: 8.7 }, // telescope
] as const;

export interface ObservatoryPetState {
  id: string;
  x: number;
  z: number;
  facing: number;
  assignedStation: PetAssignmentStation | null;
}

export interface ObservatoryPlayerState {
  x: number;
  z: number;
  px: number;
  pz: number;
  facing: number;
  moving: boolean;
  dashing: boolean;
  abilityPulse: boolean;
}

const PLAYER_RADIUS = 0.55;
const MIN_X = -16.5;
const MAX_X = 16.5;
const MIN_Z = -11.2;
const MAX_Z = 11.2;
const MOVE_SPEED = 7;
const DASH_SPEED = 18;
const DASH_SECONDS = 0.2;
const BUILD_GRID = 0.5;
let placementSerial = 0;

export interface ObservatoryBuildPreview extends HousePlacement {
  valid: boolean;
  editingId: string | null;
}

function overlaps(x: number, z: number, r: number, box: RectCollider): boolean {
  const cx = Math.max(box.x0, Math.min(box.x1, x));
  const cz = Math.max(box.z0, Math.min(box.z1, z));
  const dx = x - cx;
  const dz = z - cz;
  return dx * dx + dz * dz < r * r;
}

export class ObservatorySession {
  readonly player: ObservatoryPlayerState = { x: -5, z: 2, px: -5, pz: 2, facing: 0, moving: false, dashing: false, abilityPulse: false };
  readonly pets: ObservatoryPetState[];
  readonly placements: HousePlacement[];
  buildPreview: ObservatoryBuildPreview | null = null;
  private dashTime = 0;
  private abilityCooldown = 0;
  private moveX = 1;
  private moveZ = 0;
  private elapsed = 0;

  constructor(ownedPets: readonly string[], readonly character: CharacterId = 'knight', readonly cosmetics: readonly string[] = [], readonly assignments: Partial<Record<PetAssignmentStation, string | null>> = {}, placements: readonly HousePlacement[] = []) {
    this.pets = ownedPets.map((id, i) => ({ id, x: 6 + i * 2.1, z: 3 + (i % 2) * 2, facing: 0, assignedStation: (Object.entries(assignments).find(([, pet]) => pet === id)?.[0] as PetAssignmentStation | undefined) ?? null }));
    this.placements = placements.map((placement) => ({ ...placement }));
  }

  tick(dt: number, input: InputFrame): ObservatoryStation | null {
    const p = this.player;
    p.px = p.x;
    p.pz = p.z;
    p.abilityPulse = false;
    this.elapsed += dt;
    this.abilityCooldown = Math.max(0, this.abilityCooldown - dt);

    const length = Math.hypot(input.mx, input.my);
    if (length > 0.001) {
      this.moveX = input.mx / length;
      this.moveZ = input.my / length;
      p.facing = Math.atan2(this.moveZ, this.moveX);
    }
    if (input.dash && this.abilityCooldown <= 0) {
      this.abilityCooldown = characterDef(this.character).ability.cooldown;
      p.abilityPulse = true;
      if (this.character === 'knight' || this.character === 'rogue') this.dashTime = DASH_SECONDS;
    }
    const speed = this.dashTime > 0 ? DASH_SPEED : MOVE_SPEED;
    this.dashTime = Math.max(0, this.dashTime - dt);
    p.dashing = this.dashTime > 0;
    p.moving = length > 0.001 || p.dashing;
    const dx = (length > 0.001 ? input.mx / length : p.dashing ? this.moveX : 0) * speed * dt;
    const dz = (length > 0.001 ? input.my / length : p.dashing ? this.moveZ : 0) * speed * dt;
    this.moveAxis(dx, 0);
    this.moveAxis(0, dz);
    this.updatePets();
    return input.action ? this.nearStation() : null;
  }

  nearStation(): ObservatoryStation | null {
    let nearest: ObservatoryStation | null = null;
    let best = Infinity;
    for (const station of OBSERVATORY_STATIONS) {
      const distance = Math.hypot(this.player.x - station.x, this.player.z - station.z);
      if (distance <= station.radius && distance < best) {
        nearest = station;
        best = distance;
      }
    }
    return nearest;
  }

  beginBuild(moduleId: string, editingId: string | null = null): boolean {
    const module = HOUSE_MODULE_BY_ID.get(moduleId);
    if (!module) return false;
    const existing = editingId ? this.placements.find((placement) => placement.id === editingId) : undefined;
    const x = existing?.x ?? Math.round(this.player.x / BUILD_GRID) * BUILD_GRID;
    const z = existing?.z ?? Math.round(this.player.z / BUILD_GRID) * BUILD_GRID;
    const rotation = existing?.rotation ?? 0;
    this.buildPreview = { id: existing?.id ?? `module-${Date.now().toString(36)}-${(++placementSerial).toString(36)}`, moduleId, x, z, rotation, editingId, valid: false };
    this.buildPreview.valid = this.canPlace(this.buildPreview);
    return true;
  }

  /** Move a placement ghost with normal movement input; ability rotates 90°, interact confirms. */
  tickBuild(dt: number, input: InputFrame): 'placed' | null {
    const preview = this.buildPreview;
    if (!preview) return null;
    if (Math.hypot(input.mx, input.my) > 0.12) {
      preview.x = Math.round((preview.x + input.mx * dt * 6) / BUILD_GRID) * BUILD_GRID;
      preview.z = Math.round((preview.z + input.my * dt * 6) / BUILD_GRID) * BUILD_GRID;
    }
    if (input.dash) preview.rotation = (preview.rotation + Math.PI / 2) % (Math.PI * 2);
    preview.valid = this.canPlace(preview);
    if (!input.action || !preview.valid) return null;
    const placement: HousePlacement = { id: preview.id, moduleId: preview.moduleId, x: preview.x, z: preview.z, rotation: preview.rotation };
    const index = this.placements.findIndex((entry) => entry.id === preview.editingId);
    if (index >= 0) this.placements[index] = placement;
    else this.placements.push(placement);
    this.buildPreview = null;
    return 'placed';
  }

  cancelBuild(): void { this.buildPreview = null; }

  removePlacement(id: string): boolean {
    const index = this.placements.findIndex((placement) => placement.id === id);
    if (index < 0) return false;
    this.placements.splice(index, 1);
    return true;
  }

  canPlace(placement: Pick<HousePlacement, 'id' | 'moduleId' | 'x' | 'z' | 'rotation'>): boolean {
    const module = HOUSE_MODULE_BY_ID.get(placement.moduleId);
    if (!module) return false;
    const size = rotatedFootprint(module, placement.rotation);
    const box: RectCollider = { x0: placement.x - size.width / 2, x1: placement.x + size.width / 2, z0: placement.z - size.depth / 2, z1: placement.z + size.depth / 2 };
    const inside = box.x0 >= MIN_X + 0.4 && box.x1 <= MAX_X - 0.4 && box.z0 >= MIN_Z + 0.4 && box.z1 <= MAX_Z - 0.4;
    if (!inside) return false;
    if (module.zone === 'interior' && box.x1 > 3.7) return false;
    if (module.zone === 'yard' && box.x0 < 4.8) return false;
    if (COLLIDERS.some((fixed) => rectsOverlap(box, fixed, 0.25))) return false;
    if (OBSERVATORY_STATIONS.some((station) => circleRectOverlap(station.x, station.z, 1.25, box))) return false;
    if (circleRectOverlap(this.player.x, this.player.z, 1.15, box)) return false;
    for (const other of this.placements) {
      if (other.id === placement.id) continue;
      const otherDef = HOUSE_MODULE_BY_ID.get(other.moduleId);
      if (!otherDef) continue;
      const otherSize = rotatedFootprint(otherDef, other.rotation);
      const otherBox = { x0: other.x - otherSize.width / 2, x1: other.x + otherSize.width / 2, z0: other.z - otherSize.depth / 2, z1: other.z + otherSize.depth / 2 };
      if (rectsOverlap(box, otherBox, 0.2)) return false;
    }
    return true;
  }

  /** Test/support hook and future spawn-point API. */
  setPlayerPosition(x: number, z: number): void {
    this.player.x = this.player.px = Math.max(MIN_X, Math.min(MAX_X, x));
    this.player.z = this.player.pz = Math.max(MIN_Z, Math.min(MAX_Z, z));
  }

  private moveAxis(dx: number, dz: number): void {
    const p = this.player;
    const nx = Math.max(MIN_X, Math.min(MAX_X, p.x + dx));
    const nz = Math.max(MIN_Z, Math.min(MAX_Z, p.z + dz));
    if (!this.solidColliders().some((box) => overlaps(nx, nz, PLAYER_RADIUS, box))) {
      p.x = nx;
      p.z = nz;
    }
  }

  private solidColliders(): RectCollider[] {
    const colliders = [...COLLIDERS];
    for (const placement of this.placements) {
      const module = HOUSE_MODULE_BY_ID.get(placement.moduleId);
      if (!module?.solid) continue;
      const size = rotatedFootprint(module, placement.rotation);
      colliders.push({ x0: placement.x - size.width / 2, x1: placement.x + size.width / 2, z0: placement.z - size.depth / 2, z1: placement.z + size.depth / 2 });
    }
    return colliders;
  }

  private updatePets(): void {
    this.pets.forEach((pet, i) => {
      const phase = i * 2.17;
      const oldX = pet.x;
      const oldZ = pet.z;
      const assignedHome: Record<PetAssignmentStation, [number, number]> = { chronolab: [-6.3, -5.5], garden: [7.5, -4.4], wardrobe: [-10.2, 7.8] };
      const home = pet.assignedStation ? assignedHome[pet.assignedStation] : null;
      const homeX = home?.[0] ?? 7.5 + (i % 2) * 3;
      const homeZ = home?.[1] ?? 3 + Math.floor(i / 2) * 3;
      const roam = pet.assignedStation ? 0.65 : 2.2;
      pet.x = homeX + Math.sin(this.elapsed * (0.42 + i * 0.05) + phase) * roam;
      pet.z = homeZ + Math.cos(this.elapsed * (0.35 + i * 0.04) + phase) * (pet.assignedStation ? 0.5 : 1.8);
      if (!pet.assignedStation && Math.hypot(this.player.x - pet.x, this.player.z - pet.z) < 3.2) {
        pet.x += (this.player.x - pet.x) * 0.18;
        pet.z += (this.player.z - pet.z) * 0.18;
      }
      pet.facing = Math.atan2(pet.z - oldZ, pet.x - oldX);
    });
  }
}

function rectsOverlap(a: RectCollider, b: RectCollider, gap = 0): boolean {
  return a.x0 < b.x1 + gap && a.x1 > b.x0 - gap && a.z0 < b.z1 + gap && a.z1 > b.z0 - gap;
}

function circleRectOverlap(x: number, z: number, radius: number, box: RectCollider): boolean {
  return overlaps(x, z, radius, box);
}
